// 平台與類型字典。快照中以 bitmask 表示，所以**只能在尾端新增，不能刪除或調換順序**，
// 否則舊快照與使用者存下的篩選條件會對錯位。顯示名稱在前端 i18n，這裡只放穩定的 key。

export interface DictEntry<K extends string> {
  key: K;
  /** 對應的 IGDB id（platforms / genres） */
  igdb: readonly number[];
}

export const PLATFORMS = [
  { key: 'pc', igdb: [6] },
  { key: 'ps5', igdb: [167] },
  { key: 'ps4', igdb: [48] },
  { key: 'xsx', igdb: [169] },
  { key: 'xone', igdb: [49] },
  { key: 'switch', igdb: [130] },
  { key: 'switch2', igdb: [508] },
  { key: 'mac', igdb: [14] },
  { key: 'linux', igdb: [3] },
  { key: 'ios', igdb: [39] },
  { key: 'android', igdb: [34] },
] as const satisfies readonly DictEntry<string>[];

export const GENRES = [
  { key: 'rpg', igdb: [12] },
  { key: 'adventure', igdb: [31] },
  { key: 'shooter', igdb: [5] },
  { key: 'fighting', igdb: [4] },
  { key: 'platform', igdb: [8] },
  { key: 'puzzle', igdb: [9] },
  { key: 'racing', igdb: [10] },
  { key: 'rts', igdb: [11] },
  { key: 'simulator', igdb: [13] },
  { key: 'sport', igdb: [14] },
  { key: 'strategy', igdb: [15] },
  { key: 'tbs', igdb: [16] },
  { key: 'tactical', igdb: [24] },
  { key: 'hack-slash', igdb: [25] },
  { key: 'indie', igdb: [32] },
  { key: 'arcade', igdb: [33] },
  { key: 'music', igdb: [7] },
  { key: 'point-click', igdb: [2] },
  { key: 'visual-novel', igdb: [34] },
  { key: 'card-board', igdb: [35] },
  { key: 'moba', igdb: [36] },
  { key: 'quiz', igdb: [26] },
  { key: 'pinball', igdb: [30] },
] as const satisfies readonly DictEntry<string>[];

export type PlatformKey = (typeof PLATFORMS)[number]['key'];
export type GenreKey = (typeof GENRES)[number]['key'];

/** JS 位元運算是 32 位元有號整數，字典最多 31 項。 */
export const MAX_DICT_SIZE = 31;

export function toMask<K extends string>(dict: readonly DictEntry<K>[], keys: Iterable<K>): number {
  let mask = 0;
  for (const k of keys) {
    const i = dict.findIndex((e) => e.key === k);
    if (i >= 0) mask |= 1 << i;
  }
  return mask;
}

export function fromMask<K extends string>(dict: readonly DictEntry<K>[], mask: number): K[] {
  const out: K[] = [];
  dict.forEach((e, i) => {
    if (mask & (1 << i)) out.push(e.key);
  });
  return out;
}

/** 是否有任一交集（篩選平台用：選了 PC 與 PS5，只要支援其一就符合）。 */
export const intersects = (a: number, b: number): boolean => (a & b) !== 0;

/** IGDB id → 字典 key；不在字典中的（例如已停產平台）回傳 undefined。 */
export function fromIgdb<K extends string>(dict: readonly DictEntry<K>[], igdbId: number): K | undefined {
  return dict.find((e) => e.igdb.includes(igdbId))?.key;
}
